import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  tableCellClasses,
  styled,
  Collapse,
  Tooltip,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import Chatbot from '../components/Chatbot';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'rgba(220, 20, 60, 0.08)',
    color: '#dc143c',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.875rem',
    color: '#3a3532',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  '&:hover': {
    backgroundColor: 'rgba(220, 20, 60, 0.04)',
  },
  '& td': {
    borderColor: 'rgba(220, 20, 60, 0.1)',
  },
}));

const DropzoneBox = styled(Box)(({ theme }) => ({
  border: '2px dashed rgba(220, 20, 60, 0.4)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#dc143c',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transform: 'translateY(-2px)',
  },
}));

const formatScore = (score) => {
  if (score === 'N/A') return 'N/A';
  const scoreNum = parseFloat(score);
  if (isNaN(scoreNum)) return 'N/A';
  return `${(scoreNum * 100).toFixed(1)}%`;
};

const getScoreColor = (score) => {
  if (score === 'N/A') return { bg: 'rgba(0, 0, 0, 0.1)', text: '#666666' };
  const scoreNum = parseFloat(score);
  if (isNaN(scoreNum)) return { bg: 'rgba(0, 0, 0, 0.1)', text: '#666666' };

  if (scoreNum >= 0.85) {
    return {
      bg: 'rgba(46, 125, 50, 0.2)',
      text: '#2e7d32'
    };
  } else if (scoreNum >= 0.70) {
    return {
      bg: 'rgba(46, 125, 50, 0.1)',
      text: '#2e7d32'
    };
  } else if (scoreNum >= 0.55) {
    return {
      bg: 'rgba(237, 108, 2, 0.15)',
      text: '#ed6c02'
    };
  } else if (scoreNum >= 0.40) {
    return {
      bg: 'rgba(237, 108, 2, 0.1)',
      text: '#ed6c02'
    };
  } else {
    return {
      bg: 'rgba(211, 47, 47, 0.1)',
      text: '#d32f2f'
    };
  }
};

const getScoreText = (score) => {
  if (score === 'N/A') return "No score available";
  const scoreNum = parseFloat(score);
  if (isNaN(scoreNum)) return "Invalid score";
  
  if (scoreNum >= 0.85) return "Excellent match for role and skills";
  if (scoreNum >= 0.70) return "Strong match with required qualifications";
  if (scoreNum >= 0.55) return "Good match with some gaps";
  if (scoreNum >= 0.40) return "Partial match or similar role";
  return "Limited match or different role type";
};

const UploadWizard = () => {
  const [resumes, setResumes] = useState([]);
  const [jd, setJd] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUpload, setShowUpload] = useState(true);

  const { getRootProps: getResumeRootProps, getInputProps: getResumeInputProps } = useDropzone({
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    onDrop: acceptedFiles => {
      setResumes(acceptedFiles);
      setError(null);
    }
  });

  const { getRootProps: getJdRootProps, getInputProps: getJdInputProps } = useDropzone({
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      setJd(acceptedFiles[0]);
      setError(null);
    }
  });

  const handleUpload = async () => {
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const resumeForm = new FormData();
      resumes.forEach(file => resumeForm.append('file', file));
      const resumeResponse = await fetch('https://api-resumex.experionglobal.dev/uploadpdf', {
        method: 'POST',
        body: resumeForm,
        credentials: 'include'
      });

      if (!resumeResponse.ok) throw new Error('Failed to upload resumes');
      setUploadProgress(50);

      if (jd) {
        const jdForm = new FormData();
        jdForm.append('file', jd);
        const jdResponse = await fetch('https://api-resumex.experionglobal.dev/uploadjd', {
          method: 'POST',
          body: jdForm,
          credentials: 'include'
        });
        if (!jdResponse.ok) throw new Error('Failed to upload JD');
      }
      setUploadProgress(75);

      const processResponse = await fetch('https://api-resumex.experionglobal.dev/extract', {
        method: 'POST',
        credentials: 'include'
      });

      if (!processResponse.ok) throw new Error('Failed to process files');
      const data = await processResponse.json();
      setResults(data);
      setShowUpload(false);
      setUploadProgress(100);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearFiles = async () => {
    try {
      const response = await fetch('https://api-resumex.experionglobal.dev/clear', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        // Handle 400 error for no files uploaded
        if (response.status === 400) {
          const data = await response.json();
          if (data.error && data.error === "No files uploaded for this session") {
            setError("No files uploaded");
          } else {
            setError(data.error || 'Failed to clear files');
          }
        } else {
          // For all other errors, display the backend error if available
          const data = await response.json().catch(() => ({}));
          setError(data.error || 'Failed to clear files');
        }
        return;
      }

      setResumes([]);
      setJd(null);
      setResults([]);
      setError(null);
      setUploadProgress(0);
      setShowUpload(true);
    } catch (err) {
      setError('Failed to clear files: ' + err.message);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar 
        hasResults={results.length > 0}
        onClearResults={clearFiles}
      />
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Collapse in={showUpload}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              mb: 4,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 4,
              border: '1px solid rgba(220, 20, 60, 0.1)',
              boxShadow: '0 8px 32px rgba(220, 20, 60, 0.1)',
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#3a3532' }}>
              Upload Files
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#3a3532' }}>
                  Upload Resumes (PDF)
                </Typography>
                <DropzoneBox {...getResumeRootProps()}>
                  <input {...getResumeInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#dc143c', mb: 2 }} />
                  <Typography sx={{ color: '#3a3532' }}>
                    {resumes.length > 0
                      ? `${resumes.length} file(s) selected`
                      : 'Drag & drop resumes here (PDF or DOCX), or click to select'}
                  </Typography>
                </DropzoneBox>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#3a3532' }}>
                  Upload Job Description (PDF)
                </Typography>
                <DropzoneBox {...getJdRootProps()}>
                  <input {...getJdInputProps()} />
                  <UploadFileIcon sx={{ fontSize: 40, color: '#dc143c', mb: 2 }} />
                  <Typography sx={{ color: '#3a3532' }}>
                    {jd ? jd.name : 'Drag & drop JD here (PDF or DOCX), or click to select'}
                  </Typography>
                </DropzoneBox>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={resumes.length === 0 || !jd || loading}
                startIcon={<AnalyticsIcon />}
                sx={{
                  backgroundColor: '#dc143c',
                  '&:hover': {
                    backgroundColor: '#b01030',
                  }
                }}
              >
                Analyze Files
              </Button>
              <Button
                variant="outlined"
                onClick={clearFiles}
                disabled={loading}
                startIcon={<DeleteIcon />}
                sx={{
                  color: '#dc143c',
                  borderColor: '#dc143c',
                  '&:hover': {
                    borderColor: '#dc143c',
                    backgroundColor: 'rgba(220, 20, 60, 0.04)',
                  }
                }}
              >
                Clear Files
              </Button>
            </Box>

            {loading && (
              <Box sx={{ width: '100%', mb: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{
                    backgroundColor: 'rgba(220, 20, 60, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#dc143c',
                    }
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, color: '#3a3532' }}>
                  {uploadProgress}% Complete
                </Typography>
              </Box>
            )}

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-icon': {
                    color: '#dc143c',
                  }
                }}
              >
                {error}
              </Alert>
            )}
          </Paper>
        </Collapse>

        {results.length > 0 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ color: '#3a3532', fontWeight: 600 }}>
                Analysis Results
              </Typography>
              <Button
                startIcon={showUpload ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                onClick={() => setShowUpload(!showUpload)}
                sx={{ color: '#dc143c' }}
              >
                {showUpload ? 'Hide Upload' : 'Show Upload'}
              </Button>
            </Box>
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 4,
                border: '1px solid rgba(220, 20, 60, 0.1)',
                boxShadow: '0 8px 32px rgba(220, 20, 60, 0.1)',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Candidate Name</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    {/* <StyledTableCell>Mobile</StyledTableCell> */}
                    <StyledTableCell>Job Role</StyledTableCell>
                    <StyledTableCell>Education</StyledTableCell>
                    <StyledTableCell>Skills</StyledTableCell>
                    <StyledTableCell>Experience</StyledTableCell>
                    {/* <StyledTableCell>Domain</StyledTableCell> */}
                    <StyledTableCell>
                      Match Score
                      <Tooltip title="Calculated based on role match, skills, and experience">
                        <HelpOutlineIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle', opacity: 0.7 }} />
                      </Tooltip>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{row['Candidate Name'] || 'N/A'}</StyledTableCell>
                      <StyledTableCell>{row['Email'] || 'N/A'}</StyledTableCell>
                      {/* <StyledTableCell>{row['Mobile'] || 'N/A'}</StyledTableCell> */}
                      <StyledTableCell>{row['Job Role'] || 'N/A'}</StyledTableCell>
                      <StyledTableCell>{row['Education'] || 'N/A'}</StyledTableCell>
                      <StyledTableCell>{row['Skills'] || 'N/A'}</StyledTableCell>
                      <StyledTableCell>{row['Years of Experience'] || 'N/A'}</StyledTableCell>
                      {/* <StyledTableCell>{row['Domain'] || 'N/A'}</StyledTableCell> */}
                      <StyledTableCell>
                        <Tooltip title={getScoreText(row.similarity_score)}>
                          <Box 
                            sx={{ 
                              minWidth: 60,
                              height: 24,
                              px: 1,
                              bgcolor: getScoreColor(row.similarity_score).bg,
                              color: getScoreColor(row.similarity_score).text,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              fontWeight: 600
                            }}
                          >
                            {formatScore(row.similarity_score)}
                          </Box>
                        </Tooltip>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
      <Chatbot />
    </Box>
  );
};

export default UploadWizard;
